import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Plus, 
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightSmall,
  Clock,
  Edit,
  Trash2,
  Repeat,
  Lightbulb,
  MoveHorizontal,
  MoveRight,
  ExternalLink,
  Bot,
  Target,
  X,
  Calendar as CalendarIcon,
  Compass,
  Check
} from 'lucide-react';
import { format, addDays, addMonths, nextMonday } from 'date-fns';
import { displayWeekStart } from '@/lib/weekKeys';
import { SubtaskProgressChip, SubtaskChecklist } from '@/components/shared/Subtasks';
import PriorityCheckbox from '@/components/shared/PriorityCheckbox';
import { HighlightRing } from '@/components/shared/SharedTasksSection';
import { formatTime12h } from '../../../utils/timeFormat';
import DayTimeline from './DayTimeline';
import SuggestionsNotesPanel from '../SuggestionsNotesPanel';
import { useDesign } from '@/contexts/DesignContext';
import { StCard, StNav, priorityDot } from '@/components/studio/planner/parts';

// Reusable sidebar context section (exported for use by WeekView)
export function ContextSection({ icon: Icon, title, subtitle, items, onToggle, onNavigate, emptyText, accentColor, renderActions, linkLabel }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isStudio } = useDesign();
  const colorMap = {
    blue: {
      gradient: 'from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10',
      icon: 'text-blue-500 dark:text-blue-400',
      title: 'text-blue-900 dark:text-blue-300',
      subtitle: 'text-blue-600 dark:text-blue-400',
      chevron: 'text-blue-400',
      checkBorder: 'border-blue-300 dark:border-blue-500/40 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500',
      empty: 'text-blue-400 dark:text-blue-300/70',
      hoverBg: 'hover:bg-blue-100/50 dark:hover:bg-blue-500/10',
    },
    indigo: {
      gradient: 'from-indigo-50 to-purple-50 dark:from-indigo-500/10 dark:to-purple-500/10',
      icon: 'text-indigo-500 dark:text-indigo-400',
      title: 'text-indigo-900 dark:text-indigo-300',
      subtitle: 'text-indigo-600 dark:text-indigo-400',
      chevron: 'text-indigo-400',
      checkBorder: 'border-indigo-300 dark:border-indigo-500/40 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500',
      empty: 'text-indigo-400 dark:text-indigo-300/70',
      hoverBg: 'hover:bg-indigo-100/50 dark:hover:bg-indigo-500/10',
    },
    amber: {
      gradient: 'from-amber-50 to-orange-50 dark:from-amber-500/10 dark:to-orange-500/10',
      icon: 'text-amber-500 dark:text-amber-400',
      title: 'text-amber-900 dark:text-amber-300',
      subtitle: 'text-amber-600 dark:text-amber-400',
      chevron: 'text-amber-400',
      checkBorder: 'border-amber-300 dark:border-amber-500/40 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500',
      empty: 'text-amber-400 dark:text-amber-300/70',
      hoverBg: 'hover:bg-amber-100/50 dark:hover:bg-amber-500/10',
    },
    emerald: {
      gradient: 'from-emerald-50 to-teal-50 dark:from-emerald-500/10 dark:to-teal-500/10',
      icon: 'text-emerald-500 dark:text-emerald-400',
      title: 'text-emerald-900 dark:text-emerald-300',
      subtitle: 'text-emerald-600 dark:text-emerald-400',
      chevron: 'text-emerald-400',
      checkBorder: 'border-emerald-300 dark:border-emerald-500/40 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500',
      empty: 'text-emerald-400 dark:text-emerald-300/70',
      hoverBg: 'hover:bg-emerald-100/50 dark:hover:bg-emerald-500/10',
    },
  };

  const c = colorMap[accentColor] || colorMap.indigo;

  // Studio (106): the tinted context card becomes an ordinary Studio card with a
  // planner-hue tile, a text link instead of the chevron, and plain rows.
  if (isStudio) {
    return (
      <StCard
        tile="planner"
        icon={<Icon />}
        title={title}
        small={subtitle}
        right={<button type="button" className="st-lnk" onClick={onNavigate} data-testid={`context-nav-${accentColor}`}>{linkLabel || 'Open'}</button>}
        testId={`context-${accentColor}`}
      >
        {items.length > 0 ? (
          <div>
            {items.map((item) => (
              <div key={item.id} className={`st-prow${item.completed ? ' st-done' : ''}`} onClick={onNavigate}>
                <button
                  type="button"
                  className={`st-cb${item.completed ? ' st-on' : ''}`}
                  aria-label={item.completed ? 'Mark not done' : 'Mark done'}
                  onClick={(e) => { e.stopPropagation(); onToggle(item); }}
                  data-testid={`context-check-${item.id}`}
                >
                  {item.completed ? <Check /> : null}
                </button>
                <span className="st-txt">
                  <span>{item.label}</span>
                  {item.badge && <span className="st-chip">{item.badge}</span>}
                </span>
                <span className="st-time">{item.completed ? 'Done' : ''}</span>
                {renderActions && <span className="st-acts">{renderActions(item)}</span>}
              </div>
            ))}
          </div>
        ) : (
          <p className="st-lbl" style={{ padding: '4px 0' }}>{emptyText}</p>
        )}
      </StCard>
    );
  }

  return (
    <Card className="shadow-sm border border-slate-200 dark:border-white/10 bg-card h-full" data-testid={`context-${accentColor}`}>
      <CardHeader className="pb-2 pt-4 px-4">
        <button
          onClick={onNavigate}
          className="flex items-center justify-between w-full group"
          data-testid={`context-nav-${accentColor}`}
        >
          <CardTitle className={`text-sm font-semibold flex items-center gap-2 ${c.title}`}>
            <Icon className={`w-4 h-4 ${c.icon}`} />
            {title}
          </CardTitle>
          <ChevronRightSmall className={`w-4 h-4 ${c.chevron} group-hover:translate-x-0.5 transition-transform`} />
        </button>
        {subtitle && (
          <CardDescription className={`text-xs ${c.subtitle} mt-0.5 ml-6`}>
            {subtitle}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {items.length > 0 ? (
          <div className="space-y-1">
            {items.map((item) => (
              <div
                key={item.id}
                className={`group flex items-center gap-2.5 py-1.5 px-2 rounded-lg transition-colors ${c.hoverBg}`}
              >
                <Checkbox
                  checked={item.completed}
                  onCheckedChange={() => onToggle(item)}
                  className={`${c.checkBorder}`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`context-check-${item.id}`}
                />
                <button
                  onClick={onNavigate}
                  className="flex-1 text-left min-w-0"
                >
                  <span className={`text-sm leading-snug ${item.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>
                    {item.label}
                  </span>
                  {item.badge && (
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/25 text-indigo-700 dark:text-indigo-200 font-medium">
                      {item.badge}
                    </span>
                  )}
                </button>
                {renderActions && renderActions(item)}
              </div>
            ))}
          </div>
        ) : (
          <p className={`text-xs ${c.empty} py-2 ml-6`}>{emptyText}</p>
        )}
      </CardContent>
    </Card>
  );
}

function DayView({
  highlightSharedId, // R32: id to ring + scroll into view (search / deep link)
  weekStartsOn = 0, // 0 Sunday | 1 Monday (2026-09-07)
  onMovePriority, // weekly priority → next/previous week
  selectedDate,
  setSelectedDate,
  currentDate,
  getTasksForDate,
  getEventsForDate,
  toggleTaskComplete,
  handleEditTask,
  handleDeleteClick,
  showAddTask,
  setShowAddTask,
  newTask,
  setNewTask,
  addTask,
  getSuggestionsForDate,
  addSuggestion,
  handleMoveTask,
  suggestedTasksCollapsed,
  setSuggestedTasksCollapsed,
  panelMode,
  setPanelMode,
  dailyNotes,
  saveDailyNote,
  setHoveredItem,
  // Context sidebar props
  weeklyPriorities,
  setWeeklyPriorities,
  monthlyPriorities,
  toggleMonthlyPriorityComplete,
  annualGoals,
  toggleAnnualGoalComplete,
  onNavigateToView,
  // Timeline props
  onCommitTaskTime,
  moveTaskToDate,
  bulkDeleteTasks,
  bulkMoveTasks,
  subtasksApi,
  sharedTasksNode,
}) {
  const { isStudio } = useDesign();
  const [expandedSubtasks, setExpandedSubtasks] = useState(() => new Set());
  const toggleSubtaskExpand = (taskId) => {
    setExpandedSubtasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };
  // ── Multi-select (shift-click range / cmd-click toggle) ──
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const selectAnchorRef = useRef(null);
  const [bulkDatePickerOpen, setBulkDatePickerOpen] = useState(false);

  const clearSelection = () => {
    setSelectedIds(new Set());
    selectAnchorRef.current = null;
    setBulkDatePickerOpen(false);
  };

  // Esc clears; changing days clears. Capture phase so this runs BEFORE
  // Radix's own Esc handling — if any dialog/menu is open, that Esc belongs
  // to it and the selection must survive the cancel.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"], [role="alertdialog"][data-state="open"]')) return;
      // Esc inside a text field (planner search, inline inputs) belongs to that field.
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      clearSelection();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { clearSelection(); }, [format(selectedDate, 'yyyy-MM-dd')]);

  const handleSelectClick = (task, e, orderedIds) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const anchor = selectAnchorRef.current;
      if (e.shiftKey && anchor && orderedIds.includes(anchor)) {
        const a = orderedIds.indexOf(anchor);
        const b = orderedIds.indexOf(task.id);
        const [lo, hi] = a < b ? [a, b] : [b, a];
        for (let i = lo; i <= hi; i++) next.add(orderedIds[i]);
      } else {
        if (next.has(task.id)) next.delete(task.id); else next.add(task.id);
        selectAnchorRef.current = task.id;
      }
      return next;
    });
    if (!selectAnchorRef.current) selectAnchorRef.current = task.id;
  };

  const handleBulkMove = async (target) => {
    await bulkMoveTasks?.(activeSelection, format(target, 'yyyy-MM-dd'));
    clearSelection();
  };

  const handleBulkDelete = () => {
    bulkDeleteTasks?.(activeSelection);
    clearSelection();
  };

  // Quick-move a task relative to the day being viewed
  const handleQuickMove = (task, when) => {
    let target;
    if (when === 'tomorrow') target = addDays(selectedDate, 1);
    else if (when === 'nextWeek') target = nextMonday(selectedDate);
    else target = addMonths(selectedDate, 1);
    moveTaskToDate?.(task.id, format(target, 'yyyy-MM-dd'));
    toast.success(`Task moved to ${format(target, 'EEE, MMM d')}`);
  };
  // Navigation helpers
  const goToPrevDay = () => setSelectedDate(addDays(selectedDate, -1));
  const goToNextDay = () => setSelectedDate(addDays(selectedDate, 1));

  const dayTasks = getTasksForDate(selectedDate);
  // Selection pruned to tasks still visible today (a per-row trash delete can
  // leave dead ids in selectedIds — never count or act on those).
  const activeSelection = dayTasks.filter(t => selectedIds.has(t.id)).map(t => t.id);
  const dayEvents = getEventsForDate(selectedDate);
  const completedCount = dayTasks.filter(t => t.completed).length;
  const goToToday = () => setSelectedDate(new Date());

  // Normalize data for ContextSection
  const weeklyItems = (weeklyPriorities || [])
    .filter(p => p.text?.trim())
    .map(p => ({ id: p.id, label: p.text, completed: p.completed }));

  const monthlyItems = (monthlyPriorities || [])
    .map(p => ({ id: p.id, label: p.title, completed: p.completed }));

  const goalItems = [...(annualGoals || [])]
    .sort((a, b) => {
      const order = { 'Q1': 1, 'Q2': 2, 'Q3': 3, 'Q4': 4 };
      return (order[a.quarter] || 5) - (order[b.quarter] || 5);
    })
    .map(g => ({ id: g.id, label: g.title, completed: g.completed, badge: g.quarter || null }));

  // Toggle handlers
  const handleWeeklyToggle = (item) => {
    if (!setWeeklyPriorities) return;
    setWeeklyPriorities((prev) =>
      prev.map(p => p.id === item.id ? { ...p, completed: !p.completed } : p)
    );
  };

  const handleMonthlyToggle = (item) => {
    if (!toggleMonthlyPriorityComplete) return;
    const original = monthlyPriorities.find(p => p.id === item.id);
    if (original) toggleMonthlyPriorityComplete(original.id, original.completed);
  };

  const handleGoalToggle = (item) => {
    if (!toggleAnnualGoalComplete) return;
    const original = annualGoals.find(g => g.id === item.id);
    if (original) toggleAnnualGoalComplete(original.id, original.completed);
  };

  // Suggested tasks / daily notes panel (shared between desktop and mobile)
  const suggestedTasksContent = (
    <SuggestionsNotesPanel
      selectedDate={selectedDate}
      collapsed={suggestedTasksCollapsed}
      setCollapsed={setSuggestedTasksCollapsed}
      mode={panelMode}
      setMode={setPanelMode}
      getSuggestionsForDate={getSuggestionsForDate}
      addSuggestion={addSuggestion}
      handleMoveTask={handleMoveTask}
      dailyNotes={dailyNotes}
      saveDailyNote={saveDailyNote}
      variant="day"
    />
  );

  // Context cards: Weekly + Monthly side-by-side, Annual Goals full-width below
  const contextSections = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ContextSection
          icon={Target}
          title="Weekly Priorities"
          subtitle={`Week of ${format(displayWeekStart(selectedDate, weekStartsOn), 'MMM d')}`}
          renderActions={onMovePriority ? (item) => (
            !item.completed && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    className="h-6 w-6 shrink-0 rounded-md flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-100/60 dark:hover:bg-blue-500/15 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    aria-label="Move priority"
                    data-testid={`wp-move-${item.id}`}
                  >
                    <MoveRight className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMovePriority(item.id, 1); }} data-testid={`wp-move-next-${item.id}`}>Move to next week</DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMovePriority(item.id, -1); }} data-testid={`wp-move-prev-${item.id}`}>Move to previous week</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )
          ) : undefined}
          items={weeklyItems}
          onToggle={handleWeeklyToggle}
          onNavigate={() => onNavigateToView?.('week')}
          emptyText="No priorities set for this week"
          accentColor="blue"
        />
        <ContextSection
          icon={CalendarIcon}
          title={`${format(selectedDate, 'MMMM')} Priorities`}
          items={monthlyItems}
          onToggle={handleMonthlyToggle}
          onNavigate={() => onNavigateToView?.('month')}
          emptyText="No priorities set for this month"
          accentColor="indigo"
        />
      </div>
      <ContextSection
        icon={Compass}
        title={`${currentDate.getFullYear()} Goals`}
        items={goalItems}
        onToggle={handleGoalToggle}
        onNavigate={() => onNavigateToView?.('year')}
        emptyText="No annual goals set"
        accentColor="emerald"
      />
    </div>
  );

  // ── Studio (106 §2, Day option A as revised by the owner) ────────────────────
  // The date, the day nav and Add task sit in the tasks-card header so the card
  // starts on the same line as the timeline; 2026 goals moves under the timeline.
  // Every rule of the classic day view is kept: overdue roll-forward and ordering
  // come from the parent, completed tasks stay in the list, shift/cmd multi-select
  // and the bulk bar, hover actions, drag into the timeline, sub-task checklists.
  if (isStudio) {
    const stTaskRow = (task) => {
      const st = subtasksApi ? subtasksApi.getForTask(task) : { items: [], checkedIds: [] };
      const selected = selectedIds.has(task.id);
      return (
        <React.Fragment key={task.id}>
          <div
            draggable
            onDragStart={(e) => { e.dataTransfer.setData('text/plain', task.id); e.dataTransfer.effectAllowed = 'move'; }}
            className={`st-prow${task.completed ? ' st-done' : ''}${selected ? ' st-sel' : ''}`}
            onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
            onClick={(e) => {
              if (e.shiftKey || e.metaKey || e.ctrlKey || activeSelection.length > 0) {
                e.preventDefault();
                handleSelectClick(task, e, dayTasks.map(t => t.id));
              } else {
                handleEditTask(task);
              }
            }}
            onMouseEnter={() => setHoveredItem?.({ type: 'task', id: task.id, data: task })}
            onMouseLeave={() => setHoveredItem?.(null)}
            data-testid={`task-item-${task.id}`}
          >
            {highlightSharedId === task.id && <HighlightRing className="rounded-lg" />}
            <button
              type="button"
              className={`st-cb${task.completed ? ' st-on' : ''}`}
              aria-label={task.completed ? 'Mark not done' : 'Mark done'}
              onClick={(e) => { e.stopPropagation(); toggleTaskComplete(task.id, task.completed); }}
              data-testid={`task-check-${task.id}`}
            >
              {task.completed ? <Check /> : null}
            </button>
            <span className="st-pd" style={{ background: priorityDot(task.priority) }} />
            <span className="st-txt">
              <span>{task.title}</span>
              {st.items.length > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  className="st-chip"
                  data-testid="subtask-progress-chip"
                  onClick={(e) => { e.stopPropagation(); toggleSubtaskExpand(task.id); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleSubtaskExpand(task.id); } }}
                >
                  {st.checkedIds.length} / {st.items.length}
                </span>
              )}
            </span>
            {/* D24 (120, owner re-review 2026-09-18): time -> repeat marker ->
                Overdue, Overdue holding the right-hand column. Reordered in the DOM
                rather than with CSS `order` so reading order matches what is drawn;
                this is the Studio branch only, classic's order is untouched. */}
            <span className="st-time">{task.time ? formatTime12h(task.time) : (task.completed ? 'Done' : '')}</span>
            {/* D3 (121 §5, owner 2026-09-23): the chip reads "Overdue" only — the
                " · Fri" suffix repeated a fact the row already carries and made the
                chip the widest thing in the row. Studio branch only. */}
            {task.isOverdue && (
              <span className="st-over">Overdue</span>
            )}
            <span className="st-repslot">
              {(task.is_recurring || task.parent_task_id) && <Repeat className="st-rep" />}
            </span>
            {/* O6 (120): glyph-only row actions, one of them destructive, carry the
                kit tooltip (`[data-ui="tooltip"]`, studio.css §7) with no delay —
                the same ruling the owner gave for the composer tools in D21. */}
            <TooltipProvider delayDuration={0}>
              <span className="st-acts">
                <DropdownMenu>
                  <Tooltip>
                    <DropdownMenuTrigger asChild>
                      <TooltipTrigger asChild>
                        <button type="button" className="st-ibtn" aria-label="Move task"
                                onClick={(e) => e.stopPropagation()} data-testid={`move-task-${task.id}`}>
                          <MoveRight />
                        </button>
                      </TooltipTrigger>
                    </DropdownMenuTrigger>
                    <TooltipContent>Move task</TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickMove(task, 'tomorrow'); }} data-testid={`move-task-tomorrow-${task.id}`}>Move to tomorrow</DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickMove(task, 'nextWeek'); }} data-testid={`move-task-next-week-${task.id}`}>Move to next week (Mon)</DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickMove(task, 'nextMonth'); }} data-testid={`move-task-next-month-${task.id}`}>Move to next month</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="st-ibtn" aria-label="Edit task"
                            onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}><Edit /></button>
                  </TooltipTrigger>
                  <TooltipContent>Edit task</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="st-ibtn" aria-label="Delete task"
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(task); }}><Trash2 /></button>
                  </TooltipTrigger>
                  <TooltipContent>Delete task</TooltipContent>
                </Tooltip>
              </span>
            </TooltipProvider>
          </div>
          {expandedSubtasks.has(task.id) && st.items.length > 0 && (
            <SubtaskChecklist
              items={st.items}
              checkedIds={st.checkedIds}
              onToggle={(subId, checked) => subtasksApi.toggleCheck(st.taskKey, task.id, subId, checked)}
            />
          )}
        </React.Fragment>
      );
    };

    const isTodayView = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
    const headSmall = [isTodayView ? 'Today' : null, dayTasks.length > 0 ? `${completedCount} of ${dayTasks.length} done` : null]
      .filter(Boolean).join(' · ');

    return (
      <div className="st-plan st-day">
        <div className="st-colmain">
          <section className="st-card" data-testid="day-tasks-card">
            <div className="st-wh">
              <h2 className="st-wt">
                {format(selectedDate, 'EEEE, MMMM d')}
                {headSmall ? <small>{headSmall}</small> : null}
              </h2>
              <div className="st-wr">
                <StNav
                  label="Today"
                  onPrev={goToPrevDay}
                  onToday={goToToday}
                  onNext={goToNextDay}
                  prevTestId="prev-day-btn"
                  todayTestId="today-btn"
                  nextTestId="next-day-btn"
                />
                <button type="button" className="st-btn st-pri st-sm" onClick={() => setShowAddTask(true)} data-testid="add-task-btn">
                  <Plus />Add task
                </button>
              </div>
            </div>
            <div className="st-wb">
              {selectedIds.size > 0 && (
                <div className="st-bulk" data-testid="bulk-action-bar">
                  <span data-testid="bulk-selected-count">{activeSelection.length} selected</span>
                  <span className="st-sp" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="st-btn st-sec st-sm" data-testid="bulk-move-btn"><MoveRight />Move to</button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleBulkMove(addDays(selectedDate, 1))} data-testid="bulk-move-tomorrow">Tomorrow</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleBulkMove(nextMonday(selectedDate))} data-testid="bulk-move-next-week">Next week (Mon)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleBulkMove(addMonths(selectedDate, 1))} data-testid="bulk-move-next-month">Next month</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => setTimeout(() => setBulkDatePickerOpen(true), 50)} data-testid="bulk-move-pick-date">
                        <CalendarIcon className="w-3.5 h-3.5 mr-1.5" /> Pick a date…
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Dialog open={bulkDatePickerOpen} onOpenChange={setBulkDatePickerOpen}>
                    <DialogContent className="w-auto max-w-fit p-4" data-testid="bulk-move-date-picker">
                      <DialogHeader className="pb-1">
                        <DialogTitle className="text-base">Move {activeSelection.length} task{activeSelection.length > 1 ? 's' : ''} to…</DialogTitle>
                      </DialogHeader>
                      <Calendar mode="single" selected={undefined} onSelect={(d) => { if (d) { setBulkDatePickerOpen(false); handleBulkMove(d); } }} initialFocus />
                    </DialogContent>
                  </Dialog>
                  <button type="button" className="st-btn st-sm st-danger" onClick={handleBulkDelete} data-testid="bulk-delete-btn"><Trash2 />Delete</button>
                  <button type="button" className="st-ibtn" style={{ width: 28, height: 28 }} onClick={clearSelection} title="Clear selection (Esc)" data-testid="bulk-clear-btn"><X /></button>
                </div>
              )}

              {dayTasks.length > 0 ? (
                <div>{dayTasks.map(stTaskRow)}</div>
              ) : (
                <div className="st-empty">
                  <span className="st-glyph"><CalendarIcon /></span>
                  <b>No tasks yet</b>
                  <span>What needs to get done today?</span>
                  <button type="button" className="st-btn st-pri st-sm" style={{ marginTop: 10 }}
                          onClick={() => setShowAddTask(true)} data-testid="add-first-task-btn">
                    <Plus />Plan your day
                  </button>
                </div>
              )}

              {sharedTasksNode}

              {dayEvents.length > 0 && (
                <>
                  <div className="st-seclabel">From your calendar<span className="st-cnt">{dayEvents.length}</span></div>
                  <div>
                    {dayEvents.map(event => (
                      <a
                        key={event.id}
                        href={event.html_link || undefined}
                        target={event.html_link ? '_blank' : undefined}
                        rel="noopener noreferrer"
                        className="st-prow"
                      >
                        <span className="st-caldot" />
                        <span className="st-txt">
                          <span>{event.title}</span>
                          <span className="st-chip">{event.all_day ? 'All day' : 'Calendar'}</span>
                        </span>
                        <span className="st-time">
                          {event.all_day ? '' : format(new Date(event.start), 'h:mm a')}
                        </span>
                        <span className="st-repslot" />
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>

          {suggestedTasksContent}

          <div className="st-two">
            <ContextSection
              icon={Target}
              title="Weekly priorities"
              subtitle={`Week of ${format(displayWeekStart(selectedDate, weekStartsOn), 'MMM d')}`}
              linkLabel="Open week"
              renderActions={onMovePriority ? (item) => (
                !item.completed && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="st-ibtn" onClick={(e) => e.stopPropagation()} aria-label="Move priority" data-testid={`wp-move-${item.id}`}>
                        <MoveRight />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMovePriority(item.id, 1); }} data-testid={`wp-move-next-${item.id}`}>Move to next week</DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMovePriority(item.id, -1); }} data-testid={`wp-move-prev-${item.id}`}>Move to previous week</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              ) : undefined}
              items={weeklyItems}
              onToggle={handleWeeklyToggle}
              onNavigate={() => onNavigateToView?.('week')}
              emptyText="No priorities set for this week"
              accentColor="blue"
            />
            <ContextSection
              icon={CalendarIcon}
              title={`${format(selectedDate, 'MMMM')} priorities`}
              linkLabel="Open month"
              items={monthlyItems}
              onToggle={handleMonthlyToggle}
              onNavigate={() => onNavigateToView?.('month')}
              emptyText="No priorities set for this month"
              accentColor="indigo"
            />
          </div>
        </div>

        <aside className="st-colside">
          {/* the timeline keeps the app's own 5 AM – 11 PM window and 64px hour */}
          <div className="hidden lg:block" data-testid="timeline-column" style={{ height: 'calc(100vh - 160px)' }}>
            <DayTimeline
              selectedDate={selectedDate}
              tasks={dayTasks}
              events={dayEvents}
              onToggleComplete={toggleTaskComplete}
              onEditTask={handleEditTask}
              onCommitTaskTime={onCommitTaskTime}
            />
          </div>
          <ContextSection
            icon={Compass}
            title={`${currentDate.getFullYear()} goals`}
            linkLabel="Open year"
            items={goalItems}
            onToggle={handleGoalToggle}
            onNavigate={() => onNavigateToView?.('year')}
            emptyText="No annual goals set"
            accentColor="emerald"
          />
        </aside>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Left Column: Tasks → Suggested (collapsed by default) → Weekly+Monthly → Annual */}
      <div className="lg:col-span-7 space-y-6">
        {/* Main Task Card */}
        <Card className="shadow-lg border-0 bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-sm" data-testid="day-tasks-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold text-slate-800 dark:text-slate-100">
                {format(selectedDate, 'EEEE, MMM d')}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={goToPrevDay} data-testid="prev-day-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="h-9" onClick={goToToday} data-testid="today-btn">Today</Button>
                <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={goToNextDay} data-testid="next-day-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            
            {/* Calendar Events Section — mobile only (desktop renders these on the timeline) */}
            {dayEvents.length > 0 && (
              <div className="mb-4 p-3 bg-slate-100 dark:bg-[#3a3d41] rounded-xl lg:hidden">
                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">Calendar Events</div>
                <div className="space-y-0">
                  {dayEvents.map(event => (
                    <a 
                      key={event.id} 
                      href={event.html_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 py-0.5 px-2 rounded-lg hover:bg-white/50 transition-colors"
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: event.color }} />
                      <span className="text-xs text-slate-500 dark:text-slate-400 w-16 flex-shrink-0">
                        {event.all_day ? 'All day' : format(new Date(event.start), 'h:mm a')}
                      </span>
                      <span className="text-sm text-slate-700 dark:text-slate-200 flex-1 truncate">{event.title}</span>
                      <ExternalLink className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Tasks Header with Add Button — stays on top even when shared tasks exist */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-700 dark:text-slate-200">Tasks</h3>
                {dayTasks.length > 0 && (
                  <span className="text-xs text-slate-400 dark:text-slate-500 font-normal">{completedCount} of {dayTasks.length} done</span>
                )}
              </div>
              <Button size="sm" className="bg-indigo-500 hover:bg-indigo-600" onClick={() => setShowAddTask(true)} data-testid="add-task-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            {/* Shared tasks bubble — team-assigned tasks live in their own space */}
            {sharedTasksNode}

            {/* Bulk action bar — appears when tasks are shift/cmd-selected */}
            {selectedIds.size > 0 && (
              <div
                className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200 dark:border-indigo-500/40"
                data-testid="bulk-action-bar"
              >
                <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300 flex-1" data-testid="bulk-selected-count">
                  {activeSelection.length} selected
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="bulk-move-btn">
                      <MoveRight className="w-3.5 h-3.5 mr-1" /> Move to
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleBulkMove(addDays(selectedDate, 1))} data-testid="bulk-move-tomorrow">
                      Tomorrow
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBulkMove(nextMonday(selectedDate))} data-testid="bulk-move-next-week">
                      Next week (Mon)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBulkMove(addMonths(selectedDate, 1))} data-testid="bulk-move-next-month">
                      Next month
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setTimeout(() => setBulkDatePickerOpen(true), 50)} data-testid="bulk-move-pick-date">
                      <CalendarIcon className="w-3.5 h-3.5 mr-1.5" /> Pick a date…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Dialog open={bulkDatePickerOpen} onOpenChange={setBulkDatePickerOpen}>
                  <DialogContent className="w-auto max-w-fit p-4" data-testid="bulk-move-date-picker">
                    <DialogHeader className="pb-1">
                      <DialogTitle className="text-base">Move {activeSelection.length} task{activeSelection.length > 1 ? 's' : ''} to…</DialogTitle>
                    </DialogHeader>
                    <Calendar
                      mode="single"
                      selected={undefined}
                      onSelect={(d) => { if (d) { setBulkDatePickerOpen(false); handleBulkMove(d); } }}
                      initialFocus
                    />
                  </DialogContent>
                </Dialog>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/40 hover:bg-red-50 dark:hover:bg-red-500/10"
                  onClick={handleBulkDelete}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={clearSelection} title="Clear selection (Esc)" data-testid="bulk-clear-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Task List — list rows are draggable into the timeline (HTML5 DnD, desktop only) */}
            <div className="space-y-1.5">
              {dayTasks.map(task => {
                const st = subtasksApi ? subtasksApi.getForTask(task) : { items: [], checkedIds: [] };
                return (
                <React.Fragment key={task.id}>
                <div
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', task.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  className={`relative group flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-all ${
                    selectedIds.has(task.id)
                      ? 'bg-indigo-50 dark:bg-indigo-500/20 border-indigo-400 dark:border-indigo-400/70 ring-1 ring-indigo-400 dark:ring-indigo-400/70'
                      : task.isOverdue && !task.completed
                      ? 'bg-amber-50 dark:bg-amber-500/15 border-amber-200 dark:border-amber-500/40 hover:border-amber-300 dark:hover:border-amber-400/60 hover:shadow-md hover:-translate-y-px' 
                      : 'bg-elevated border-slate-200 dark:border-white/[0.08] hover:border-indigo-200 dark:hover:border-indigo-400/40 hover:shadow-md hover:-translate-y-px dark:hover:bg-[#3a3d41]'
                  }`}
                  onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
                  onClick={(e) => {
                    if (e.shiftKey || e.metaKey || e.ctrlKey || activeSelection.length > 0) {
                      e.preventDefault();
                      handleSelectClick(task, e, dayTasks.map(t => t.id));
                    } else {
                      handleEditTask(task);
                    }
                  }}
                  onMouseEnter={() => setHoveredItem?.({ type: 'task', id: task.id, data: task })}
                  onMouseLeave={() => setHoveredItem?.(null)}
                  data-testid={`task-item-${task.id}`}
                >
                  {highlightSharedId === task.id && <HighlightRing className="rounded-xl" />}
                  <PriorityCheckbox
                    priority={task.priority}
                    checked={task.completed}
                    onCheckedChange={() => toggleTaskComplete(task.id, task.completed)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`task-check-${task.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium flex items-center gap-2 ${task.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-100'}`}>
                      <span className="truncate">{task.title}</span>
                      {(task.is_recurring || task.parent_task_id) && (
                        <Repeat className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                      )}
                      {st.items.length > 0 && (
                        <SubtaskProgressChip
                          done={st.checkedIds.length}
                          total={st.items.length}
                          expanded={expandedSubtasks.has(task.id)}
                          onClick={() => toggleSubtaskExpand(task.id)}
                        />
                      )}
                      {task.isOverdue && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-500/25 text-amber-800 dark:text-amber-200 font-medium flex-shrink-0">
                          Overdue
                        </span>
                      )}
                    </div>
                    {task.time && (
                      <div className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatTime12h(task.time)}
                      </div>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                        title="Move task"
                        data-testid={`move-task-${task.id}`}
                      >
                        <MoveRight className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 hover:text-indigo-500" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickMove(task, 'tomorrow'); }} data-testid={`move-task-tomorrow-${task.id}`}>
                        Move to tomorrow
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickMove(task, 'nextWeek'); }} data-testid={`move-task-next-week-${task.id}`}>
                        Move to next week (Mon)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickMove(task, 'nextMonth'); }} data-testid={`move-task-next-month-${task.id}`}>
                        Move to next month
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}>
                    <Edit className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 hover:text-indigo-500" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleDeleteClick(task); }}>
                    <Trash2 className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 hover:text-red-500" />
                  </Button>
                </div>
                {expandedSubtasks.has(task.id) && st.items.length > 0 && (
                  <SubtaskChecklist
                    items={st.items}
                    checkedIds={st.checkedIds}
                    onToggle={(subId, checked) => subtasksApi.toggleCheck(st.taskKey, task.id, subId, checked)}
                  />
                )}
                </React.Fragment>
                );
              })}
              {dayTasks.length === 0 && (
                <div className="text-center py-6 px-4">
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">No tasks yet</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">What needs to get done today?</p>
                  <Button 
                    size="sm" 
                    className="bg-indigo-500 hover:bg-indigo-600"
                    onClick={() => setShowAddTask(true)}
                    data-testid="add-first-task-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Plan Your Day
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Suggested Tasks (collapsed by default in this layout) */}
        {suggestedTasksContent}

        {/* Context cards: Weekly + Monthly side-by-side, Annual full-width below */}
        {contextSections}
      </div>

      {/* Right Column: Timeline (desktop only, lg+) — sticky with own scroll */}
      <div className="hidden lg:block lg:col-span-5" data-testid="timeline-column">
        <div
          className="sticky"
          style={{ top: 88, height: 'calc(100vh - 108px)', maxHeight: 'calc(100vh - 108px)' }}
        >
          <DayTimeline
            selectedDate={selectedDate}
            tasks={dayTasks}
            events={dayEvents}
            onToggleComplete={toggleTaskComplete}
            onEditTask={handleEditTask}
            onCommitTaskTime={onCommitTaskTime}
          />
        </div>
      </div>
    </div>
  );
}

export default DayView;
